import * as React from "react";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";

import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";
import { IconButton } from "./icon-button";

/**
 * v2 Alert (redesign phase 5, #455): the inline banner. Storybook only until
 * implementation; see docs/design-library/04-implementation-notes.md.
 *
 * One look (break #104): the Badge tones' soft tint (`--{tone}-subtle`) with
 * the strong text (`--{tone}-strong`) for title, text and icon, NO border (v1's
 * bordered info box read as a card). Radius 8, between controls (6) and cards
 * (12), as in v1. Title Sora 14/20 Semibold, text Inter 14/20, icon 16.
 *
 * Four tones, no `ai` tone: an alert reports status. AI-ness is the marker's
 * job; a problem with AI output is a warning or an error.
 *
 * Role by tone: `error` is `role="alert"` (assertive), the others are
 * `role="status"` (polite; a warning about an answer is not urgent). Static
 * notices that are part of the page when it loads pass `announce={false}`.
 *
 * Actions are secondary controls (`outline` / `ghost` Buttons `sm`), never the
 * blue main action. They sit right of the text while it fits and wrap below it
 * on narrow widths. Dismissing is controlled: `onDismiss` fires, the caller
 * removes the alert.
 */

export type AlertTone = "info" | "success" | "warning" | "error";

const TONE: Record<AlertTone, { className: string; icon: IconComponent }> = {
  info: { className: "bg-info-subtle text-info-strong", icon: Info },
  success: { className: "bg-success-subtle text-success-strong", icon: CircleCheck },
  warning: { className: "bg-warning-subtle text-warning-strong", icon: TriangleAlert },
  error: { className: "bg-destructive-subtle text-destructive-strong", icon: CircleAlert },
};

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: AlertTone;
  /** Optional bold first line. */
  title?: React.ReactNode;
  /** Replaces the tone's icon. */
  icon?: IconComponent;
  /** Buttons `size="sm"`, `outline` or `ghost`. */
  actions?: React.ReactNode;
  /** Shows a "Schließen" button; the caller removes the alert. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Live region by tone (default). `false` for notices present at page load. */
  announce?: boolean;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    tone = "info",
    title,
    icon,
    actions,
    onDismiss,
    dismissLabel = "Schließen",
    announce = true,
    className,
    children,
    ...props
  },
  ref,
) {
  const { className: toneClass, icon: toneIcon } = TONE[tone];
  return (
    <div
      ref={ref}
      role={announce ? (tone === "error" ? "alert" : "status") : undefined}
      data-tone={tone}
      className={cn("flex items-start gap-2 rounded-lg p-3", toneClass, className)}
      {...props}
    >
      {/* 16px icon centred on the first 20px line. */}
      <Icon icon={icon ?? toneIcon} className="mt-0.5" />
      <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-60">
          {title != null && <p className="font-sans text-label">{title}</p>}
          {children != null && <div className="font-body text-body">{children}</div>}
        </div>
        {/* 28px buttons centred on the first line: -4 above and below. */}
        {actions != null && <div className="-my-1 flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {onDismiss && (
        <IconButton icon={X} label={dismissLabel} size="xs" className="-my-0.5 -mr-1" onClick={onDismiss} />
      )}
    </div>
  );
});

export { Alert };
